// Approval requests — the permission slip.
//
// A request is raised before the thing it authorises exists: the cashier asks
// to sell on tempo, and there is no invoice yet to point at. So an approved
// request belongs to the person who asked, and the next matching action they
// take spends it. That is what the counter actually does — the manager comes
// over, says yes, the sale goes through.
//
// Every write here is an RPC. There is deliberately no insert policy on
// approval_requests: a row the browser could write directly is a row the
// browser could write already-approved.

import { supabase } from '@/lib/supabase';
import type { Json } from '@/types/database';

export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

export interface ApprovalRule {
  kind: string;
  label: string;
  description: string;
  enabled: boolean;
  requiredLevel: number;
  threshold: number | null;
}

export interface ApprovalRequest {
  id: string;
  kind: string;
  status: ApprovalStatus;
  requiredLevel: number;
  currentLevel: number;
  reason: string;
  payload: Record<string, unknown>;
  eventId: string | null;
  requestedBy: string | null;
  requestedByName: string | null;
  createdAt: number;
  resolvedAt: number | null;
  consumedAt: number | null;
}

function mapRequest(r: Record<string, unknown>): ApprovalRequest {
  return {
    id: r.id as string,
    kind: r.kind as string,
    status: r.status as ApprovalStatus,
    requiredLevel: r.required_level as number,
    currentLevel: r.current_level as number,
    reason: (r.reason as string) ?? '',
    payload: (r.payload as Record<string, unknown>) ?? {},
    eventId: (r.event_id as string | null) ?? null,
    requestedBy: (r.requested_by as string | null) ?? null,
    requestedByName: (r.requested_by_name as string | null) ?? null,
    createdAt: new Date(r.created_at as string).getTime(),
    resolvedAt: r.resolved_at ? new Date(r.resolved_at as string).getTime() : null,
    consumedAt: r.consumed_at ? new Date(r.consumed_at as string).getTime() : null,
  };
}

export async function listApprovalRules(): Promise<ApprovalRule[]> {
  const { data, error } = await supabase.from('approval_rules').select('*').order('label');
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => ({
    kind: r.kind,
    label: r.label,
    description: r.description,
    enabled: r.enabled,
    requiredLevel: r.required_level,
    threshold: r.threshold,
  }));
}

export async function setApprovalRule(
  kind: string,
  patch: { enabled?: boolean; requiredLevel?: number; threshold?: number | null },
): Promise<void> {
  const { error } = await supabase
    .from('approval_rules')
    .update({
      ...(patch.enabled !== undefined ? { enabled: patch.enabled } : {}),
      ...(patch.requiredLevel !== undefined ? { required_level: patch.requiredLevel } : {}),
      ...(patch.threshold !== undefined ? { threshold: patch.threshold } : {}),
    })
    .eq('kind', kind);
  if (error) throw new Error(error.message);
}

/**
 * Everything this session can see: their own requests, plus — for an approver
 * — everything inside their rayon. The split is done by RLS, not here, so the
 * list cannot be widened by editing this query.
 */
export async function listApprovalRequests(status?: ApprovalStatus): Promise<ApprovalRequest[]> {
  let q = supabase.from('approval_requests').select('*').order('created_at', { ascending: false });
  if (status) q = q.eq('status', status);
  const { data, error } = await q.limit(200);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapRequest(r as unknown as Record<string, unknown>));
}

/**
 * Requests of one kind about one entity — `payload.<key> = id`. The invoice
 * page asks "is there a void request for me", the customer page "is an edit
 * pending"; both read the same slip the inbox shows.
 */
export async function listRequestsFor(
  kind: string,
  key: string,
  id: string,
): Promise<ApprovalRequest[]> {
  const { data, error } = await supabase
    .from('approval_requests')
    .select('*')
    .eq('kind', kind)
    .eq(`payload->>${key}`, id)
    .order('created_at', { ascending: false })
    .limit(20);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapRequest(r as unknown as Record<string, unknown>));
}

/**
 * Propose changes to a customer. The server diffs against the row, normalises
 * the phone the way the form would, and files a `customer.edit` request;
 * nothing touches `customers` until an approver signs it.
 */
export async function requestCustomerEdit(input: {
  customerId: string;
  /** Column → proposed value. Empty string clears an optional column. */
  changes: Record<string, string>;
  reason?: string;
}): Promise<ApprovalRequest> {
  const { data, error } = await supabase.rpc('request_customer_edit', {
    p_customer_id: input.customerId,
    p_changes: input.changes as Json,
    p_reason: input.reason ?? '',
  });
  if (error) throw new Error(error.message);
  return mapRequest(data as unknown as Record<string, unknown>);
}

export async function requestApproval(input: {
  kind: string;
  reason?: string;
  payload?: Record<string, unknown>;
  eventId?: string | null;
}): Promise<ApprovalRequest> {
  const { data, error } = await supabase.rpc('request_approval', {
    p_kind: input.kind,
    p_reason: input.reason ?? '',
    p_payload: (input.payload ?? {}) as Json,
    p_event_id: input.eventId ?? undefined,
  });
  if (error) throw new Error(error.message);
  return mapRequest(data as unknown as Record<string, unknown>);
}

export async function approveRequest(id: string, note = ''): Promise<ApprovalRequest> {
  const { data, error } = await supabase.rpc('approve_request', {
    p_request_id: id,
    p_note: note,
  });
  if (error) throw new Error(error.message);
  return mapRequest(data as unknown as Record<string, unknown>);
}

export async function rejectRequest(id: string, note = ''): Promise<ApprovalRequest> {
  const { data, error } = await supabase.rpc('reject_request', {
    p_request_id: id,
    p_note: note,
  });
  if (error) throw new Error(error.message);
  return mapRequest(data as unknown as Record<string, unknown>);
}

export async function cancelRequest(id: string): Promise<ApprovalRequest> {
  const { data, error } = await supabase.rpc('cancel_request', { p_request_id: id });
  if (error) throw new Error(error.message);
  return mapRequest(data as unknown as Record<string, unknown>);
}
